import { Filetype } from "idfweb-vfs";

import type { FsDirEntry } from "idfweb-runtime-protocol";

import { readDrop, type Dropped } from "./drop.ts";
import { fileIcon } from "./icons.ts";
import type { RuntimeLink } from "./runtime.ts";

/**
 * A row of the explorer. Most rows are guest paths, but a node does not have
 * to be one: a group collects rows from anywhere in the guest under a label of
 * its own, which is how the managed components and the build output are shown
 * apart from the sources. `id` is unique among the rows and keeps a node's
 * expanded state across refreshes.
 */
export interface TreeNode {
    id: string;
    label: string;
    kind: "file" | "dir" | "group";
    path?: string;
    description?: string;
    title?: string;
    dim?: boolean;
    badge?: string;
    children?: () => Promise<TreeNode[]>;
    /** What a file row without a path opens, such as a view tab of the page's. */
    open?: (preview: boolean) => void;
}

export interface ExplorerHost {
    open(path: string, preview: boolean): void;
    contextMenu(node: TreeNode, event: MouseEvent): void;
}

/** One collapsible section of the explorer, such as the project or ESP-IDF. */
export interface Section {
    id: string;
    title: string;
    root: () => Promise<TreeNode[]>;
    collapsed?: boolean;
    actions?: { icon: string; title: string; run: () => void }[];
    /**
     * Where files dragged in from the host go when dropped on `folder`, a
     * folder row of this section, or on the section itself when undefined:
     * a function that takes the drop's items, or undefined to refuse it.
     */
    dropInto?: (folder: TreeNode | undefined) => ((items: Promise<Dropped[]>) => void) | undefined;
}

/** The folder a drag from the host is over: a row, or a whole section when `row` is undefined. */
interface DropTarget {
    section: Section;
    row: Row | undefined;
    /** The rows that light up: the folder's and those under it. */
    rows: Set<string> | undefined;
    run: (items: Promise<Dropped[]>) => void;
}

interface Row {
    node: TreeNode;
    depth: number;
}

/**
 * The file tree, as VS Code's explorer draws it: 22-pixel rows, folders first,
 * a chevron for each folder, a preview tab on a single click and a pinned one
 * on a double click. Children are read when a folder is expanded and read
 * again when the runtime worker reports a change under it.
 */
export class Explorer {
    readonly element: HTMLElement;
    readonly #runtime: RuntimeLink;
    readonly #host: ExplorerHost;
    #sections: Section[] = [];
    readonly #expanded = new Set<string>();
    readonly #collapsedSections = new Set<string>();
    readonly #children = new Map<string, TreeNode[]>();
    /** Counts the times `#children` was dropped, so a read that began before one is not kept. */
    #epoch = 0;
    readonly #rows = new Map<string, Row[]>();
    #selected: string | undefined;
    #rendering: Promise<void> | undefined;
    #again = false;
    #drop: DropTarget | undefined;
    /** A collapsed folder a drag rests on opens after a moment, as in VS Code. */
    #dragOpen: { id: string; timer: ReturnType<typeof setTimeout> } | undefined;

    constructor(runtime: RuntimeLink, host: ExplorerHost) {
        this.#runtime = runtime;
        this.#host = host;
        this.element = document.createElement("div");
        this.element.className = "wb-explorer";
        this.element.tabIndex = 0;
        this.element.addEventListener("keydown", (event) => this.#key(event));
        this.element.addEventListener("dragover", (event) => this.#dragOver(event));
        this.element.addEventListener("dragleave", (event) => {
            if (!this.element.contains(event.relatedTarget as Node | null)) this.#setDrop(undefined);
        });
        this.element.addEventListener("drop", (event) => {
            if (!event.dataTransfer?.types.includes("Files")) return;
            event.preventDefault();
            const target = this.#dropTarget(event.target);
            this.#setDrop(undefined);
            target?.run(readDrop(event.dataTransfer));
        });
    }

    setSections(sections: Section[]): void {
        this.#sections = sections;
        for (const section of sections) {
            if (section.collapsed && !this.#rows.has(section.id)) this.#collapsedSections.add(section.id);
        }
        this.refresh();
    }

    /** Read every expanded folder again and redraw. */
    refresh(): void {
        this.#children.clear();
        this.#epoch++;
        void this.#render();
    }

    /** Read these folders again if they are shown. */
    changed(directories: string[]): void {
        let any = false;
        for (const id of this.#children.keys()) {
            if (directories.includes(id.replace(/^dir:/, ""))) {
                this.#children.delete(id);
                this.#epoch++;
                any = true;
            }
        }
        if (any) void this.#render();
    }

    /** Expand the folders down to `path` and select it; `expand` expands a folder at `path` too. */
    async reveal(path: string, expand = false): Promise<void> {
        let found: string[] | undefined;
        for (const section of this.#sections) {
            found = await this.#find(await section.root(), path);
            if (found === undefined) continue;
            this.#collapsedSections.delete(section.id);
            break;
        }
        if (found === undefined) return;
        const self = found.pop()!;
        for (const id of expand ? [...found, self] : found) this.#expanded.add(id);
        this.#selected = self;
        await this.#render();
        this.element.querySelector(".wb-row.selected")?.scrollIntoView({ block: "nearest" });
    }

    /** The ids of the folders down to `path` and of its own node last, or undefined. */
    async #find(nodes: TreeNode[], path: string): Promise<string[] | undefined> {
        for (const node of nodes) {
            if (node.path === path) return [node.id];
            if (node.kind === "dir" && node.path !== undefined && path.startsWith(node.path + "/")) {
                const below = await this.#find(await this.#childrenOf(node), path);
                if (below !== undefined) return [node.id, ...below];
            }
        }
        return undefined;
    }

    async #childrenOf(node: TreeNode): Promise<TreeNode[]> {
        let children = this.#children.get(node.id);
        if (children === undefined) {
            const epoch = this.#epoch;
            children = (await node.children?.().catch(() => [])) ?? [];
            if (epoch === this.#epoch) this.#children.set(node.id, children);
        }
        return children;
    }

    async #render(): Promise<void> {
        if (this.#rendering !== undefined) {
            this.#again = true;
            return await this.#rendering;
        }
        this.#rendering = (async () => {
            do {
                this.#again = false;
                await this.#draw();
            } while (this.#again);
        })();
        try {
            await this.#rendering;
        } finally {
            this.#rendering = undefined;
        }
    }

    async #draw(): Promise<void> {
        const parts: HTMLElement[] = [];
        for (const section of this.#sections) {
            const collapsed = this.#collapsedSections.has(section.id);
            parts.push(this.#sectionHeader(section, collapsed));
            if (collapsed) continue;
            const body = document.createElement("div");
            body.className = "wb-section-body";
            body.dataset["section"] = section.id;
            body.setAttribute("role", "tree");
            const rows: Row[] = [];
            const walk = async (nodes: TreeNode[], depth: number) => {
                for (const node of nodes) {
                    rows.push({ node, depth });
                    if (node.kind !== "file" && this.#expanded.has(node.id)) {
                        await walk(await this.#childrenOf(node), depth + 1);
                    }
                }
            };
            await walk(await section.root(), 0);
            this.#rows.set(section.id, rows);
            body.append(...rows.map((row) => this.#row(row)));
            parts.push(body);
        }
        this.element.replaceChildren(...parts);
        this.#markDrop();
    }

    #sectionHeader(section: Section, collapsed: boolean): HTMLElement {
        const header = document.createElement("div");
        header.className = "wb-section-header";
        header.dataset["section"] = section.id;
        const chevron = document.createElement("span");
        chevron.className = `codicon codicon-chevron-${collapsed ? "right" : "down"}`;
        const title = document.createElement("span");
        title.className = "wb-section-title";
        title.textContent = section.title;
        header.append(chevron, title);
        const actions = document.createElement("span");
        actions.className = "wb-section-actions";
        for (const action of section.actions ?? []) {
            const button = document.createElement("button");
            button.type = "button";
            button.className = `codicon codicon-${action.icon}`;
            button.title = action.title;
            button.addEventListener("click", (event) => {
                event.stopPropagation();
                action.run();
            });
            actions.append(button);
        }
        header.append(actions);
        header.addEventListener("click", () => {
            if (collapsed) this.#collapsedSections.delete(section.id);
            else this.#collapsedSections.add(section.id);
            void this.#render();
        });
        return header;
    }

    #row({ node, depth }: Row): HTMLElement {
        const row = document.createElement("div");
        row.className = "wb-row";
        row.setAttribute("role", "treeitem");
        row.dataset["id"] = node.id;
        row.style.setProperty("--depth", String(depth));
        row.title = node.title ?? node.path ?? node.label;
        if (node.id === this.#selected) row.classList.add("selected");
        if (node.dim) row.classList.add("dim");
        const expanded = this.#expanded.has(node.id);
        if (node.kind !== "file") row.setAttribute("aria-expanded", String(expanded));

        const guides = document.createElement("span");
        guides.className = "wb-indent";
        // A file's icon takes the column a folder's chevron is in, so that
        // every name at one depth starts at the same place, as in VS Code.
        if (node.kind === "file") {
            const icon = fileIcon(node.path ?? node.label);
            icon.classList.add("wb-twistie");
            row.append(guides, icon);
        } else {
            const twistie = document.createElement("span");
            twistie.className = `wb-twistie codicon codicon-chevron-${expanded ? "down" : "right"}`;
            row.append(guides, twistie);
        }
        const label = document.createElement("span");
        label.className = "wb-row-label";
        label.textContent = node.label;
        row.append(label);
        if (node.description !== undefined) {
            const description = document.createElement("span");
            description.className = "wb-row-description";
            description.textContent = node.description;
            row.append(description);
        }
        if (node.badge !== undefined) {
            const badge = document.createElement("span");
            badge.className = "wb-row-badge";
            badge.textContent = node.badge;
            row.append(badge);
        }

        row.addEventListener("click", () => this.#activate(node, true));
        row.addEventListener("dblclick", () => {
            if (node.kind !== "file") return;
            if (node.path !== undefined) this.#host.open(node.path, false);
            else node.open?.(false);
        });
        row.addEventListener("contextmenu", (event) => {
            event.preventDefault();
            this.#selected = node.id;
            this.#markSelected();
            this.#host.contextMenu(node, event);
        });
        return row;
    }

    #activate(node: TreeNode, preview: boolean): void {
        this.#selected = node.id;
        if (node.kind === "file") {
            this.#markSelected();
            if (node.path !== undefined) this.#host.open(node.path, preview);
            else node.open?.(preview);
            return;
        }
        this.#toggle(node);
    }

    #toggle(node: TreeNode, open = !this.#expanded.has(node.id)): void {
        if (open) this.#expanded.add(node.id);
        else this.#expanded.delete(node.id);
        void this.#render();
    }

    #markSelected(): void {
        for (const row of Array.from(this.element.querySelectorAll<HTMLElement>(".wb-row"))) {
            row.classList.toggle("selected", row.dataset["id"] === this.#selected);
        }
    }

    #dragOver(event: DragEvent): void {
        if (!event.dataTransfer?.types.includes("Files")) return;
        // Taken even where nothing can be dropped, so the browser does not open the file in place of the page.
        event.preventDefault();
        const target = this.#dropTarget(event.target);
        event.dataTransfer.dropEffect = target === undefined ? "none" : "copy";
        this.#setDrop(target);
        const over = target?.row?.node;
        const rowId =
            event.target instanceof Element ? event.target.closest<HTMLElement>(".wb-row")?.dataset["id"] : undefined;
        const opens = over !== undefined && over.id === rowId && !this.#expanded.has(over.id) ? over : undefined;
        if (opens?.id === this.#dragOpen?.id) return;
        if (this.#dragOpen !== undefined) clearTimeout(this.#dragOpen.timer);
        this.#dragOpen = opens && { id: opens.id, timer: setTimeout(() => this.#toggle(opens, true), 500) };
    }

    /** Where a drop on `element` goes: a folder row, a file row's folder, a section header's section, or the first section that takes drops. */
    #dropTarget(element: EventTarget | null): DropTarget | undefined {
        const at = element instanceof Element ? element : undefined;
        const sectionId = at?.closest<HTMLElement>("[data-section]")?.dataset["section"];
        const section = this.#sections.find((s) =>
            sectionId === undefined ? s.dropInto?.(undefined) !== undefined : s.id === sectionId,
        );
        if (section?.dropInto === undefined) return undefined;
        const rows = this.#rows.get(section.id) ?? [];
        const id = at?.closest<HTMLElement>(".wb-row")?.dataset["id"];
        const index = rows.findIndex((row) => row.node.id === id);
        let row = rows[index];
        if (row?.node.kind === "file") {
            const depth = row.depth;
            row = rows
                .slice(0, index)
                .reverse()
                .find((r) => r.depth < depth);
        }
        const run = section.dropInto(row?.node);
        if (run === undefined) return undefined;
        let lit: Set<string> | undefined;
        if (row !== undefined) {
            const from = rows.indexOf(row);
            const end = rows.findIndex((r, i) => i > from && r.depth <= row!.depth);
            lit = new Set(rows.slice(from, end === -1 ? undefined : end).map((r) => r.node.id));
        }
        return { section, row, rows: lit, run };
    }

    #setDrop(target: DropTarget | undefined): void {
        const same = target?.section === this.#drop?.section && target?.row?.node.id === this.#drop?.row?.node.id;
        this.#drop = target;
        if (target === undefined && this.#dragOpen !== undefined) {
            clearTimeout(this.#dragOpen.timer);
            this.#dragOpen = undefined;
        }
        if (!same) this.#markDrop();
    }

    #markDrop(): void {
        const drop = this.#drop;
        for (const body of Array.from(this.element.querySelectorAll<HTMLElement>(".wb-section-body"))) {
            const section = body.dataset["section"];
            body.classList.toggle("drop-target", drop?.rows === undefined && drop?.section.id === section);
            for (const row of Array.from(body.querySelectorAll<HTMLElement>(".wb-row"))) {
                const lit =
                    drop !== undefined &&
                    drop.section.id === section &&
                    drop.rows?.has(row.dataset["id"] ?? "") === true;
                row.classList.toggle("drop-target", lit);
            }
        }
    }

    #key(event: KeyboardEvent): void {
        const rows = [...this.#rows.entries()]
            .filter(([id]) => !this.#collapsedSections.has(id))
            .flatMap(([, rows]) => rows);
        const at = rows.findIndex((row) => row.node.id === this.#selected);
        const row = rows[at];
        const select = (next: Row | undefined) => {
            if (next === undefined) return;
            this.#selected = next.node.id;
            this.#markSelected();
            this.element
                .querySelector(`.wb-row[data-id="${CSS.escape(next.node.id)}"]`)
                ?.scrollIntoView({ block: "nearest" });
        };
        switch (event.key) {
            case "ArrowDown":
                select(rows[at + 1] ?? rows[0]);
                break;
            case "ArrowUp":
                select(rows[at - 1] ?? rows[rows.length - 1]);
                break;
            case "ArrowRight":
                if (row && row.node.kind !== "file") {
                    if (this.#expanded.has(row.node.id)) select(rows[at + 1]);
                    else this.#toggle(row.node, true);
                }
                break;
            case "ArrowLeft":
                if (row && row.node.kind !== "file" && this.#expanded.has(row.node.id)) {
                    this.#toggle(row.node, false);
                } else if (row) {
                    select(
                        rows
                            .slice(0, at)
                            .reverse()
                            .find((r) => r.depth < row.depth),
                    );
                }
                break;
            case "Enter":
            case " ":
                if (row) this.#activate(row.node, event.key === " ");
                break;
            default:
                return;
        }
        event.preventDefault();
    }
}

/** The entries of a guest directory as rows: folders first, then by name, as VS Code sorts. */
export async function directoryNodes(
    runtime: RuntimeLink,
    path: string,
    present: (entry: FsDirEntry, path: string) => TreeNode | null = plainNode(runtime),
): Promise<TreeNode[]> {
    const entries = await runtime.fs.readdir(path);
    return entries
        .sort((a, b) => {
            const ad = a.filetype === Filetype.DIRECTORY ? 0 : 1;
            const bd = b.filetype === Filetype.DIRECTORY ? 0 : 1;
            return ad - bd || a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        })
        .map((entry) => present(entry, `${path}/${entry.name}`))
        .filter((node): node is TreeNode => node !== null);
}

export function plainNode(runtime: RuntimeLink) {
    const present = (entry: FsDirEntry, path: string): TreeNode =>
        entry.filetype === Filetype.DIRECTORY
            ? {
                  id: `dir:${path}`,
                  label: entry.name,
                  kind: "dir",
                  path,
                  children: () => directoryNodes(runtime, path, present),
              }
            : { id: `path:${path}`, label: entry.name, kind: "file", path };
    return present;
}
